import { differenceInDays, format, formatDistance, formatRelative } from "date-fns";
import { enUS, fr, nl, type Locale } from "date-fns/locale";

import { MISSING_DATE_LABEL, toValidDate, type DateLike } from "./safeDate";

/**
 * Dutch relative timestamps for PalmTree Hub.
 * date-fns' nl locale says "afgelopen maandag" and omits the time on older
 * dates. This locale keeps a 24-hour clock and the forms:
 * vandaag om 14:51, gisteren om 09:05, maandag om 14:51, 3 okt 2026 om 14:51.
 */
const nlRelative: Locale = {
  ...nl,
  formatRelative: (token) => {
    switch (token) {
      case "lastWeek":
        return "eeee 'om' p";
      case "yesterday":
        return "'gisteren om' p";
      case "today":
        return "'vandaag om' p";
      case "tomorrow":
        return "'morgen om' p";
      case "nextWeek":
        return "eeee 'om' p";
      default:
        return "d MMM yyyy 'om' p";
    }
  },
};

export const dateFnsLocale = (locale: string): Locale => {
  if (locale.startsWith("nl")) return nl;
  if (locale.startsWith("fr")) return fr;
  return enUS;
};

/** "3 okt 2026" — short Dutch month, no abbreviation dot. */
export const formatDutchDay = (date: DateLike): string => {
  const valid = toValidDate(date);
  if (!valid) return MISSING_DATE_LABEL;
  return format(valid, "d MMM yyyy", { locale: nl }).replace(/\./g, "");
};

export const formatLocalizedDate = (date: DateLike, locale = "nl") => {
  const valid = toValidDate(date);
  if (!valid) return MISSING_DATE_LABEL;
  return new Intl.DateTimeFormat(locale.startsWith("nl") ? "nl-NL" : locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(valid);
};

export const formatRelativeDate = (
  date: DateLike,
  locale = "nl",
  now = new Date(),
) => {
  const dateObj = toValidDate(date);
  if (!dateObj) return MISSING_DATE_LABEL;
  if (locale.startsWith("nl")) {
    return formatRelative(dateObj, now, { locale: nlRelative }).replace(
      /\./g,
      "",
    );
  }

  if (differenceInDays(now, dateObj) > 6) {
    return new Intl.DateTimeFormat(locale).format(dateObj);
  }
  return formatRelative(dateObj, now, { locale: dateFnsLocale(locale) });
};

export const formatDistanceLocalized = (
  date: DateLike,
  locale = "nl",
  now = new Date(),
) => {
  const valid = toValidDate(date);
  if (!valid) return MISSING_DATE_LABEL;
  return formatDistance(valid, now, {
    addSuffix: true,
    locale: dateFnsLocale(locale),
  });
};
